import { PlusOutlined, ReloadOutlined, ClearOutlined } from "@ant-design/icons";
import {
  AssetStore,
  IAssetCategoriesListModel,
  IAssetCategoriesSearchFilterModel,
  IAssetCategoryModel,
  RecordStatus,
} from "@xala/common-services";
import {
  Button,
  Empty,
  Icon,
  InputSearch,
  ITableColumnProps,
  ITableFilter,
  NotificationService,
  PageContent,
  PageHeader,
  Pagination,
  Popconfirm,
  setTableColumnSearchProps,
  TableWithDraggableSorter,
  Tag,
  useSearchParamsFilters,
  useSearchParamsPagination,
} from "@xala/common-ui";
import React, { useEffect, useState } from "react";
import { WithTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Action, ActionCreator } from "redux";
import { AssetCategoryModal } from "../AssetCategoryModal";

const notificationService = NotificationService.getInstance();

export interface IAssetCategoryListProps extends WithTranslation {
  action?: AssetStore.Types.AssetActionsTypes;
  categories: IAssetCategoriesListModel;
  isFetching: boolean;
  isProcessing: boolean;
  searchAssetCategories: ActionCreator<Action>;
  updateCategory: ActionCreator<Action>;
}

const parseBool = (value: any): boolean => value && value === "true";

const defaultCategory: IAssetCategoryModel = {
  Id: -1,
  Code: "",
  Name: "",
  Description: "",
  UpToDate: true,
  Sequence: 1,
};

const rowKey = (row: IAssetCategoryModel): string => `${row.Guid}`;

export const AssetCategoryList = ({
  categories,
  searchAssetCategories,
  action,
  t,
  updateCategory,
  isProcessing,
  isFetching,
}: IAssetCategoryListProps) => {
  const [state, setState] = useState({
    isNewCategory: true,
    isOpenFormModal: false,
    selectedCategory: { ...defaultCategory },
  });

  const location = useLocation();
  const { replace } = useHistory();
  const dispatch = useDispatch();

  const pagination = useSearchParamsPagination(categories.TotalCount);
  const filters = useSearchParamsFilters({
    FullTextSearch: "string",
    Name: "string",
    Description: "string",
    UpToDate: "boolean",
  });

  useEffect(() => {
    searchAssetCategories({
      ...filters.current,
      ...pagination.current,
    });
  }, [pagination.current, filters, location.key]);

  const refreshData = () => replace(location);

  useEffect(() => {
    switch (action?.type) {
      case AssetStore.Consts.INSERT_ASSET_CATEGORY_SUCCESS:
        refreshData();
        closeModal();
        notificationService.success({
          message: t("AddCategorySuccess", "Category added successfully."),
        });
        break;

      case AssetStore.Consts.INSERT_ASSET_CATEGORY_FAILURE:
        notificationService.error({
          message: t(
            "AddCategoryFailure",
            "There was an error while creating category."
          ),
          description: action?.error?.Message,
        });
        break;

      case AssetStore.Consts.UPDATE_ASSET_CATEGORY_SUCCESS:
        refreshData();
        closeModal();
        notificationService.success({
          message: t("UpdateCategorySuccess", "Category updated successfully."),
        });
        break;

      case AssetStore.Consts.UPDATE_ASSET_CATEGORY_FAILURE:
        notificationService.error({
          message: t(
            "UpdateCategoryFailure",
            "There was an error while updating category."
          ),
          description: action?.error?.Message,
        });
        break;

      case AssetStore.Consts.DELETE_ASSET_CATEGORY_SUCCESS:
        refreshData();
        closeModal();
        notificationService.success({
          message: t("DeleteCategorySuccess", "Category deleted successfully."),
        });
        break;

      case AssetStore.Consts.DELETE_ASSET_CATEGORY_FAILURE:
        notificationService.error({
          message: t(
            "DeleteCategoryFailure",
            "There was an error while deleting category."
          ),
          description: action?.error?.Message,
        });
        break;

      default:
        break;
    }
  }, [action?.type]);

  const onTableChange = (
    _: any,
    { Name, Description, UpToDate }: ITableFilter
  ) =>
    filters.update((data) => ({
      ...data,
      Name: Name?.[0]?.toString(),
      Description: Description?.[0]?.toString(),
      UpToDate:
        UpToDate && UpToDate?.length == 1
          ? parseBool(UpToDate?.[0])
          : undefined,
    }));

  const onSearch = (value: string) =>
    filters.update((data) => ({
      ...data,
      FullTextSearch: value || undefined,
    }));

  const closeModal = () => {
    setState((oldState) => ({ ...oldState, isOpenFormModal: false }));
  };

  const onAddNewCategoryClick = () => {
    setState((oldState) => ({
      ...oldState,
      isNewCategory: true,
      isOpenFormModal: true,
      selectedCategory: { ...defaultCategory },
    }));
  };

  const getColumnsProps = (): Array<ITableColumnProps<IAssetCategoryModel>> => {
    const filter: IAssetCategoriesSearchFilterModel = categories.Filter || {};
    return [
      {
        dataIndex: "Code",
        key: "Code",
        title: t("Code", "Code"),
        className: "drag-visible",
        render: (_: any, row: IAssetCategoryModel) => {
          return (
            <a
              onClick={() => {
                setState((oldState) => ({
                  ...oldState,
                  isNewCategory: false,
                  isOpenFormModal: true,
                  selectedCategory: row,
                }));
              }}
            >
              {row.Code}
            </a>
          );
        },
      },
      {
        dataIndex: "Name",
        key: "Name",
        title: t("Name", "Name"),
        filteredValue: filter?.Name ? [filter.Name] : null,
        ...setTableColumnSearchProps("Name"),
      },
      {
        dataIndex: "Description",
        key: "Description",
        title: t("Description", "Description"),
        filteredValue: filter?.Description ? [filter.Description] : null,
        ...setTableColumnSearchProps("Description"),
      },
      {
        dataIndex: "UpToDate",
        key: "UpToDate",
        title: t("CLASS_LIST_TABLE_UPTODATE_COLUMN", "Up to date"),
        width: "130px",
        align: "center",
        filters: [
          { text: t("CLASS_LIST_TABLE_UPTODATE_COLUMN_FILTER"), value: "true" },
          {
            text: t("CLASS_LIST_TABLE_OUTTODATE_COLUMN_FILTER"),
            value: "false",
          },
        ],
        filteredValue:
          filter.UpToDate !== undefined ? [`${filter.UpToDate}`] : null,
        render: (upToDate: boolean) => (
          <Tag color={upToDate ? "#418841" : "#c44"}>
            {upToDate
              ? t("CLASS_LIST_TABLE_UPTODATE_COLUMN")
              : t("CLASS_LIST_TABLE_OUTTODATE_COLUMN_FILTER")}
          </Tag>
        ),
      },
      {
        key: "Actions",
        dataIndex: "Actions",
        align: "center",
        width: "100px",
        title: t("TABLE_ACTIONS_COLUMN", "Actions"),
        render: (_: any, row: IAssetCategoryModel) => (
          <Popconfirm
            title={t(
              "DELETE_ELEMENT_DOUBLE_CONFIRMATION_QUESTION",
              "Are you sure you want to delete element?"
            )}
            onConfirm={async (e?: React.MouseEvent<HTMLElement>) => {
              e?.preventDefault();
              dispatch(AssetStore.Actions.deleteAssetCategory({ ...row, RecordStatus: RecordStatus.Deleted }));
            }}
            okText={t("BUTTON_YES", "Yes")}
            cancelText={t("BUTTON_NO", "No")}
          >
            <Button
              danger={true}
              icon={<Icon type="delete" />}
              title={t("DELETE_ELEMENT", "Delete element")}
            />
          </Popconfirm>
        ),
      },
    ];
  };

  const [entities, setEntities] = useState(categories.Entities);

  useEffect(() => setEntities(categories.Entities), [categories.Entities]);

  const onMoveRow = (dragIndex: number, hoverIndex: number) => {
    const draggedCategory = entities[dragIndex];
    const hoveredCategory = entities[hoverIndex];

    const categoryToUpdate: IAssetCategoryModel = {
      ...draggedCategory,
      Sequence: hoveredCategory.Sequence ?? 1,
      RecordStatus: RecordStatus.Updated,
    };

    const sequenceChanged =
      draggedCategory.Sequence !== categoryToUpdate.Sequence;
    const draggedToNewPosition = draggedCategory.Guid !== hoveredCategory.Guid;

    if (draggedToNewPosition && sequenceChanged) {
      updateCategory(categoryToUpdate);
    }
  };

  const columns = getColumnsProps();

  const renderHandleWrapper = (handle: React.ReactNode) =>
    filters.anyActive ? (
      <Popconfirm
        trigger="hover"
        placement="right"
        title={t(
          "CLEAR_FILTERS_TO_REORDER_LABEL",
          "Clear filters to reorder the list."
        )}
        onConfirm={filters.clear}
        okText={t("MENU_OPTION_CLEAR_FILTERS")}
        cancelText={t("BUTTON_CANCEL")}
      >
        {handle}
      </Popconfirm>
    ) : (
      handle
    );

  const [localSearch, setLocalSearch] = useState("");
  useEffect(() => {
    setLocalSearch(filters.current.FullTextSearch || "");
  }, [filters.current.FullTextSearch]);

  return (
    <PageContent footer={<Pagination {...pagination.props} />}>
      <PageHeader
        title={t("AssetCategoryListTitle", "Asset Categories")}
        extra={
          <>
            <InputSearch
              key="search"
              placeholder="Search"
              onChange={(e) => setLocalSearch(e.target.value)}
              value={localSearch}
              onSearch={onSearch}
              style={{ width: 250 }}
              allowClear={true}
            />
            <Button
              key="clear-filters"
              shape="circle"
              disabled={!filters.anyActive}
              icon={<ClearOutlined />}
              onClick={filters.clear}
              title="Clear filters"
            />
            <Button
              key="reload"
              shape="circle"
              icon={<ReloadOutlined />}
              onClick={refreshData}
              title="Refresh data"
            />
            <Button
              key="add"
              shape="circle"
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddNewCategoryClick}
              title={t("new", "Add new category")}
            />
          </>
        }
      />

      <TableWithDraggableSorter<IAssetCategoryModel>
        locale={{
          emptyText: (
            <>
              <Empty />
              {filters.anyActive && (
                <Button icon={<ClearOutlined />} onClick={filters.clear}>
                  {t("MENU_OPTION_CLEAR_FILTERS")}
                </Button>
              )}
            </>
          ),
        }}
        dragType="handler"
        columns={columns}
        dragDisabled={filters.anyActive}
        renderHandleWrapper={renderHandleWrapper}
        dataSource={entities}
        loading={categories.IsLoading ?? false}
        pagination={false}
        rowKey={rowKey}
        onChange={onTableChange}
        onMoveRow={onMoveRow}
      />

      <AssetCategoryModal
        loading={isProcessing || isFetching}
        visible={state.isOpenFormModal}
        selectedCategory={state.selectedCategory}
        isNewCategory={state.isNewCategory}
        closeModal={closeModal}
      />
    </PageContent>
  );
};
